<template>
  <fn-modal
    :class="[
      'modal-service-locations',
      {
        'modal-service-locations--map': isCartOrCheckout,
        'modal-service-locations--no-results': noServiceLocationsAvailable,
      },
    ]"
    :headline-text="headline"
    :loading="loading"
    adjustable-height
    keep-body-scroll-position
    @close="$emit('close')"
  >
    <template
      v-if="mapShown && isCartOrCheckout && !mobileView"
      #beforeContent
    >
      <client-only>
        <GmapMap
          ref="map"
          :api-key="apiKey"
          :center="centerCoordinates"
          :styles="mapStyles"
          :zoom="zoom"
          class="msa-map"
          disable-default-ui
          @click="mapSelectedServiceLocation = null"
        >
          <GmapMarker
            v-if="userPosition.lat && userPosition.lng"
            :options="{
              position: userPosition,
              icon: {
                url: userIconUrl,
                scaledSize: { width: 20, height: 30 },
              },
            }"
          />

          <GmapCircle
            v-if="userPosition.lat && userPosition.lng"
            :center="userPosition"
            :options="{
              strokeColor: 'transparent',
              strokeOpacity: 0,
              strokeWeight: 0,
              fillColor: '#000000',
              fillOpacity: 0.1,
            }"
            :radius="radius"
          />
          <GmapMarker
            v-for="(m, index) in serviceLocationsForRender"
            :key="index"
            :options="{
              position: {
                lat: m.geoPoint?.latitude,
                lng: m.geoPoint?.longitude,
              },
              clickable: true,
              icon: {
                url: markerIcon(m.geoPoint),
                scaledSize: { width: 20, height: 30 },
              },
            }"
            @click="loadServiceLocation(m)"
          />
        </GmapMap>
      </client-only>
    </template>

    <template #default>
      <div class="msa-scrollable">
        <client-only v-if="mapShown && isCartOrCheckout && mobileView">
          <GmapMap
            ref="map"
            :api-key="apiKey"
            :center="centerCoordinates"
            :styles="mapStyles"
            :zoom="zoom"
            class="msa-map"
            disable-default-ui
            @click="mapSelectedServiceLocation = null"
          >
            <GmapMarker
              v-if="userPosition.lat && userPosition.lng"
              :options="{
                position: userPosition,
                icon: {
                  url: userIconUrl,
                  scaledSize: { width: 20, height: 30 },
                },
              }"
            />

            <GmapCircle
              v-if="userPosition.lat && userPosition.lng"
              :center="userPosition"
              :options="{
                strokeColor: 'transparent',
                strokeOpacity: 0,
                strokeWeight: 0,
                fillColor: '#000000',
                fillOpacity: 0.1,
              }"
              :radius="radius"
            />
            <GmapMarker
              v-for="(m, index) in serviceLocationsForRender"
              :key="index"
              :options="{
                position: {
                  lat: m.geoPoint?.latitude,
                  lng: m.geoPoint?.longitude,
                },
                clickable: true,
                icon: {
                  url: markerIcon(m.geoPoint),
                  scaledSize: { width: 20, height: 30 },
                },
              }"
              @click="loadServiceLocation(m)"
            />
          </GmapMap>
        </client-only>

        <div class="msa-wrapper">
          <div class="msa-intro">
            <div
              v-if="!searchPerformed && cartSearch"
              class="i-initial-search-text"
            >
              {{ $t('storeListing.search.cartInitialText') }}
            </div>
            <fn-form-store-search
              v-if="!isPudo"
              ref="msaStoreSearch"
              :locate-me-new="isPDP"
              :map-shown="mapShown"
              :placeholder="$t('storeListing.search.placeholder')"
              :query="queryText"
              :search-payload-initial="searchQuery"
              :show-map-toggle="showMapToggle && !noServiceLocationsAvailable"
              single-section
              @submit="handleSearch"
              @locate-me="handleLocateMe"
            />

            <div
              v-if="showMapToggle"
              class="fss-map-toggle"
            >
              <fn-icon
                color="green-dark"
                icon="map"
                size="s"
              />
              <fn-link
                no-arrow
                @click="mapShown = !mapShown"
              >
                {{ mapShown ? $t('storeListing.search.listView') : $t('storeListing.search.mapView') }}
              </fn-link>
            </div>

            <fn-action-box-notification
              v-if="error"
              error
              icon="alert"
              icon-size="s"
            >
              <span>{{ error }}</span>
            </fn-action-box-notification>

            <fn-checkbox-toggle
              v-if="isPDP && clickAndCollectEnabled && !noServiceLocationsAvailable"
              :label="$t('storeListing.search.clickAndCollectStoresFilter')"
              :value="filterClickAndCollectStores"
              @input="filterClickAndCollectStores = !filterClickAndCollectStores"
            />

            <fn-headline
              v-if="modalHeadline"
              class="msa-headline"
              type="h2"
            >
              {{ modalHeadline }}
            </fn-headline>

            <fn-notification-text
              v-if="isPDP && !noServiceLocationsAvailable"
              :text="$t('product.availability.notice')"
              background-color="grey-tonal-100"
              icon="circle-i-light"
              icon-size="s"
              redesign
            />

            <div
              v-if="showNoResultsNotification"
              class="msa-no-service-locations-notification"
            >
              <fn-headline
                class="msa-headline"
                no-margin-bottom
                size="h4"
                type="h2"
              >
                {{ modalNoResultsSubHeadline }}
              </fn-headline>

              <img
                class="msa-no-service-locations-image"
                src="@/assets/img/no-services.svg"
              />

              <fn-notification-text
                :text="noResultsText"
                background-color="grey-tonal-100"
                redesign
              />
            </div>
          </div>

          <div
            v-if="!!serviceLocationsForRender?.length"
            ref="serviceLocationsListing"
            class="msa-service-locations-wrapper"
          >
            <fn-service-card
              v-for="service in serviceLocationsForRender"
              :id="`msa-service-card-${service?.storeNumber || service?.name || service?.address?.pickUpStationCode}`"
              :key="service?.storeNumber || service?.name || service?.address?.pickUpStationCode"
              :favorite-store-number="favoriteStoreNumber"
              :is-p-d-p="isPDP"
              :is-pudo="isPudo"
              :selected-service-number="
                mapSelectedServiceLocation?.storeNumber ||
                mapSelectedServiceLocation?.name ||
                mapSelectedServiceLocation?.address?.pickUpStationCode
              "
              :service="service"
              :product="product"
              tabindex="0"
              @service-card-click="loadServiceLocation"
              @focus-confirm-button="focusConfirmButton"
              @plp-service-card-click="selectStore"
            />
          </div>

          <fn-link
            v-if="isPudo && pickupStations.length > 5"
            bold
            class="msa-show-more"
            no-arrow
            @click="showAll = !showAll"
          >
            <fn-icon :icon="showAll ? 'arrow-up' : 'arrow-down'" />
            <span>{{ $t(showAll ? 'buttons.showLess' : 'buttons.showMore') }}</span>
          </fn-link>
        </div>
      </div>

      <div
        v-if="showCTAButton"
        ref="msaButtonWrapper"
        class="msa-button-wrapper"
      >
        <fn-button
          :disabled="!CTAButtonEnabled"
          :quaternary-alt="noServiceLocationsAvailable && !isPDP"
          :secondary="CTAButtonEnabled"
          class="msa-button"
          @click="onButtonClick"
        >
          {{ submitButtonText }}
        </fn-button>
      </div>
    </template>
  </fn-modal>
</template>

<script>
import { Circle as GmapCircle, GoogleMap as GmapMap, Marker as GmapMarker } from 'vue3-google-map';
import { getDeliveryAddressFromPickupStation } from '@/utils/addresses';
import GoogleMaps from '@/mixins/GoogleMaps';
import LocalStorage from '@/mixins/LocalStorage';
import PageScroll from '@/mixins/PageScroll';

export default defineComponent({
  components: {
    GmapCircle,
    GmapMap,
    GmapMarker,
  },
  mixins: [LocalStorage, PageScroll, GoogleMaps],
  props: {
    product: {
      type: Object,
      default: () => ({}),
    },
    productId: {
      type: String,
      default: null,
    },
    headline: {
      type: String,
      default: null,
    },
    searchPayloadInitial: {
      type: [String, null],
      default: null,
    },
    pickupStations: {
      type: Array,
      default: () => [],
    },
    isPudo: {
      type: Boolean,
    },
    billingAddress: {
      type: Object,
      default: () => ({}),
    },
    cartSearch: {
      type: Boolean,
    },
  },
  emits: ['close', 'selectStore'],
  data: () => ({
    searchQuery: '',
    serviceLocations: null,
    loading: false,
    error: null,
    posFavStoreNumber: null,
    posFavStore: null,
    posFavStorePostCode: null,
    mapShown: false,
    mapSelectedServiceLocation: null,
    showAll: false,
    filterClickAndCollectStores: false,
    searchPerformed: false,
  }),
  computed: {
    ...mapState(useMainStore, {
      baseStore: 'baseStore',
      isAppview: 'isAppview',
      isMobileView: 'isMobileView',
      features: 'getFeature',
      isBaseStoreFR: 'getIsBaseStoreFR',
      isBaseStorePL: 'getIsBaseStorePL',
      clickAndCollectRolloutStoresBehaviourEnabled: 'clickAndCollectRolloutStoresBehaviourEnabled',
    }),

    ...mapWritableState(useCartStore, {
      pickupAddress: 'pickupAddress',
    }),

    ...mapState(useUserStore, {
      user: 'user',
      addresses: 'addresses',
      isAuthenticated: 'getIsAuthenticated',
      isSoftLoggedIn: 'softLogin',
      favoriteStore: 'getUserFavoriteStore',
      favoriteStoreLocalPostalCode: 'favoriteStoreLocalPostalCode',
    }),

    ...mapWritableState(useUserStore, {
      posAvailabilitySearch: 'posAvailabilitySearch',
    }),

    clickAndCollectEnabled() {
      return this.features('clickAndCollectEnabled');
    },

    favoriteStoreNumber: {
      get() {
        return this.posFavStoreNumber || this.favoriteStore?.name;
      },
      set(posFavStoreNumber) {
        this.posFavStoreNumber = posFavStoreNumber;
      },
    },

    showNoResultsNotification() {
      return this.searchPerformed && this.noServiceLocationsAvailable && !this.loading;
    },

    noResultsText() {
      if (this.noClickAndCollectServiceLocationsAvailable) {
        return this.isPudo
          ? this.$t('storeListing.search.noResultsTextPickUpStation')
          : this.$t('storeListing.search.noResultsTextClickAndCollect');
      }
      return this.$t('storeListing.search.noResultsText');
    },

    isAuthenticatedAndUser() {
      return this.isAuthenticated && this.user;
    },

    queryText() {
      if (!this.isAuthenticated && this.favoriteStoreLocalPostalCode) {
        return this.favoriteStoreLocalPostalCode.postalCode;
      }

      if (this.isAuthenticated && this.favoriteStore) {
        return this.favoriteStore?.address?.postalCode;
      }

      return this.searchQuery;
    },

    isPDP() {
      return this.productId && !this.isCartOrCheckout;
    },

    isCartOrCheckout() {
      return this.$route.path.includes(this.$t('url.checkout.index')) || this.$route.path.includes(this.$t('url.cart'));
    },

    showMapToggle() {
      return this.isCartOrCheckout && this.mobileView && !this.noServiceLocationsAvailable;
    },

    noClickAndCollectServiceLocationsAvailable() {
      return (
        !this.serviceLocationsForRender?.length &&
        this.clickAndCollectEnabled &&
        this.onlyClickAndCollectServiceLocations
      );
    },

    modalHeadline() {
      if (this.isCartOrCheckout) {
        if (this.clickAndCollectEnabled && !this.noServiceLocationsAvailable && !this.isPudo) {
          return this.$t('storeListing.search.resultsTextClickAndCollect');
        }
        if (this.isPudo && this.pickupStations.length) {
          return `${this.pickupStations.length} ${this.$t('checkout.pudo.search.title')}`;
        }
      }
      return null;
    },

    modalNoResultsSubHeadline() {
      return this.isBaseStorePL && this.isPudo
        ? this.$t('storeListing.search.noResultsHeadlinePudo')
        : this.$t('storeListing.search.noResultsHeadline');
    },

    noServiceLocationsAvailable() {
      return !this.serviceLocationsForRender?.length || this.noClickAndCollectServiceLocationsAvailable;
    },

    submitButtonText() {
      if (this.noServiceLocationsAvailable && this.isCartOrCheckout) {
        return this.$t('storeListing.search.noResultsCheckoutButtonClickAndCollect');
      }
      if (this.clickAndCollectEnabled && this.isCartOrCheckout) {
        return this.$t('storeListing.search.confirmStoreSelection');
      }

      return this.$t('storeListing.search.closeSearchModal');
    },

    // If C&C is enabled and C&C toggle is true, only take first 5 stores with C&C feature enabled
    // Otherwise take first 15 stores
    serviceLocationsForRender() {
      const numberStoresDisplay = parseFloat(this.$t('storeListing.search.numberStoresDisplay'));

      const clickAndCollectFilter = (service) =>
        [SHIP_TO_STORE, PICK_FROM_STORE].includes(service?.clickAndCollectFeaturesInStore);

      if (this.clickAndCollectEnabled && this.onlyClickAndCollectServiceLocations && !this.isPudo) {
        const serviceLocations = this.isPDP
          ? this.serviceLocations?.filter(clickAndCollectFilter)
          : this.serviceLocations
              ?.map((service) => ({
                productsAvailability: service?.productsAvailability,
                ...service.store,
              }))
              ?.filter(clickAndCollectFilter);

        return serviceLocations?.slice(0, numberStoresDisplay);
      }

      if (this.isPudo) {
        const serviceLocations = this.pickupStations.map((obj) => ({
          ...obj,
          geoPoint: {
            latitude: Number(obj.coordinateLatitude),
            longitude: Number(obj.coordinateLongitude),
          },
          displayName: obj.address.pickUpStationName,
          formattedDistance: `${obj.distance.value} ${obj.distance.unit}`,
        }));
        return this.showAll ? serviceLocations : serviceLocations.slice(0, 5);
      }

      return this.serviceLocations?.slice(0, 15);
    },

    mobileView() {
      return this.isMobileView || this.isAppview;
    },

    onlyClickAndCollectServiceLocations() {
      return (
        (this.isCartOrCheckout && this.clickAndCollectEnabled) ||
        (this.isPDP && this.clickAndCollectEnabled && this.filterClickAndCollectStores)
      );
    },

    CTAButtonEnabled() {
      if (this.isPDP || this.noServiceLocationsAvailable) {
        return true;
      }

      return this.isCartOrCheckout && !this.noServiceLocationsAvailable && !!this.mapSelectedServiceLocation;
    },

    prepareServiceAsDeliveryOption() {
      const service = this.mapSelectedServiceLocation;
      const { address, name, openingHours, storeStock, productsAvailability, displayName } = service;
      const { line1, postalCode, town } = address;

      return {
        address,
        openingHours,
        storeStock,
        productsAvailability,
        firstName: service.displayName,
        street: line1,
        postcode: postalCode,
        city: town,
        addressAddition: name,
        remarks: name,
        displayName,
      };
    },

    zoom() {
      return this.isPudo ? 13 : 10;
    },

    showCTAButton() {
      if (this.isPudo) {
        return this.pickupStations.length;
      }

      return this.searchPerformed && (this.isPDP || this.isCartOrCheckout || this.mobileView);
    },
  },
  mounted() {
    this.onInitPDP();
    this.handleMapDefaultVisibility();

    if (this.searchPayloadInitial) {
      this.handleSearch(this.searchPayloadInitial);
      this.searchQuery = this.searchPayloadInitial;
    }

    if (this.isPudo && this.pickupStations.length) {
      this.handleCenterCoordinates();
    }
  },
  methods: {
    ...mapActions(useMainStore, {
      getStoreListSorted: 'getStoreListSorted',
    }),

    ...mapActions(useUserStore, {
      loadUser: 'loadUser',
      setFavoriteStore: 'setFavoriteStore',
      setFavoriteStorePostCode: 'setFavoriteStorePostCode',
    }),

    ...mapActions(useCartStore, {
      resetPickupStations: 'resetPickupStations',
      getStoresForCart: 'getStoresForCart',
    }),

    focusConfirmButton() {
      this.$nextTick(() => {
        this.$refs.msaButtonWrapper?.querySelector('button')?.focus({ focusVisible: true });
      });
    },

    handleSearch(payload) {
      this.loading = true;
      this.error = null;
      this.serviceLocations = null;

      // Close autosuggestions in Chrome
      document.querySelector(':focus')?.blur();

      const searchPayload = {
        sort: 'stock-availbility',
        ...(this.isPDP && { productId: this.productId }),
        ...(!this.isPDP &&
          this.clickAndCollectEnabled && {
            filterProperties: [
              {
                property: 'onlyClickAndCollect',
                values: ['true'],
              },
            ],
          }),
      };

      if (this.clickAndCollectRolloutStoresBehaviourEnabled && !this.isPDP && !this.isPudo) {
        searchPayload.radius = this.$t('storeListing.search.clickAndCollectStoreRadius');
      }

      if (payload.latitude) {
        searchPayload.latitude = payload.latitude;
        searchPayload.longitude = payload.longitude;
      } else {
        searchPayload.query = payload;
      }

      // getStoreListSorted - Gets availability stores for PDP
      // getStoresForCart - Gets all C&C stores with cart availability for checkout/address
      const serviceLocationsMethod = this.isPDP
        ? this.getStoreListSorted(searchPayload)
        : this.getStoresForCart(searchPayload);

      serviceLocationsMethod
        .then((response) => {
          this.posAvailabilitySearch = searchPayload;
          this.serviceLocations = this.isPDP ? response.stores : response.storesWithAvailability;

          this.handleCenterCoordinates();

          if (!this.serviceLocations.length) {
            return;
          }

          this.preselectSavedServiceLocation();
        })
        .catch(() => {
          this.error = this.$t('validation.general');
        })
        .finally(() => {
          this.loading = false;
          this.searchPerformed = true;
        });
    },

    handleCenterCoordinates() {
      if (!this.serviceLocationsForRender?.length) {
        this.centerCoordinates = this.defaultCenterCoordinates;
        return;
      }

      this.centerCoordinates = {
        lat: this.serviceLocationsForRender[0].geoPoint?.latitude,
        lng: this.serviceLocationsForRender[0].geoPoint?.longitude,
        zoom: this.zoom,
      };
    },

    handleLocateMe() {
      if (!('geolocation' in navigator)) {
        return;
      }

      this.loading = true;
      this.error = null;

      navigator.geolocation.getCurrentPosition(
        (position) => {
          const latLng = {
            latitude: position.coords.latitude,
            longitude: position.coords.longitude,
          };

          this.handleSearch(latLng);

          this.searchQuery = '';
          this.posAvailabilitySearch = latLng;
        },
        () => {
          this.error = this.$t('validation.noGps');
          this.loading = false;
        },
      );
    },

    prependFavoritePos() {
      const favStoreClone = {
        ...(this.isAuthenticated ? this.favoriteStore : this.posFavStore || this.getFromLocalStorage('favoriteStore')),
      };
      this.serviceLocations = [favStoreClone];
    },

    handleMapDefaultVisibility() {
      this.mapShown = !(this.mobileView || this.isPDP);
    },

    onInitPDP() {
      if (!this.isPDP) {
        return;
      }

      if (!this.isAuthenticated) {
        this.posFavStoreNumber = this.getFromLocalStorage('favoriteStoreNumber')?.storeNumber;

        this.posFavStorePostCode = this.getFromLocalStorage('favoriteStorePostCode');
        this.searchQuery = this.posFavStorePostCode?.postalCode || '';
      }

      if (this.favoriteStoreNumber) {
        this.prependFavoritePos();
      }
    },

    async loadServiceLocation(service) {
      if (!service) {
        return;
      }

      this.mapSelectedServiceLocation = service;

      const map = await this.$refs.map.map;

      const storeLatLng = new window.google.maps.LatLng(service.geoPoint.latitude, service.geoPoint.longitude);

      map.panTo(storeLatLng);

      const serviceNumber = this.isPudo ? service.address?.pickUpStationCode : service.storeNumber;

      this.$refs.serviceLocationsListing?.querySelector(`#msa-service-card-${serviceNumber}`)?.scrollIntoView({
        behavior: 'smooth',
      });
    },

    selectStore(service) {
      sessionStorage.setItem('selectedStore', JSON.stringify(service));
      return this.$emit('selectStore', service, this.posAvailabilitySearch);
    },

    onButtonClick() {
      if (this.noServiceLocationsAvailable) {
        return this.$emit('close');
      }

      if (this.isCartOrCheckout && this.mapSelectedServiceLocation) {
        if (this.isPudo) {
          this.setSelectedPickupStation(this.mapSelectedServiceLocation.address);
        } else {
          this.pickupAddress.fnStore = this.prepareServiceAsDeliveryOption;
        }
      }

      return this.$emit('close', true, true);
    },

    setSelectedPickupStation(pickupStation) {
      const pickupStationDeliveryAddress = getDeliveryAddressFromPickupStation(pickupStation, this.billingAddress);
      this.pickupAddress.station = pickupStationDeliveryAddress;
      this.resetPickupStations();
    },

    markerIcon(serviceLocation) {
      return this.isMarkerSelectedServiceLocation(serviceLocation) ? this.userIconUrl : this.iconUrl;
    },

    isMarkerSelectedServiceLocation({ latitude, longitude }) {
      if (!this.mapSelectedServiceLocation) {
        return false;
      }

      const { latitude: selectedLatitude, longitude: selectedLongitude } = this.mapSelectedServiceLocation.geoPoint;

      return latitude === selectedLatitude && longitude === selectedLongitude;
    },

    preselectSavedServiceLocation() {
      const savedServiceLocation = this.isAuthenticated
        ? this.addresses.find((address) => address.deliveryAddressType === DELIVERY_ADDRESS_TYPE_STORE)?.remarks ||
          this.pickupAddress.fnStore?.remarks
        : this.pickupAddress.fnStore?.remarks;

      if (!savedServiceLocation) {
        return;
      }

      const serviceLocationToPreselect = this.serviceLocationsForRender.find(
        (service) => service.name === savedServiceLocation,
      );

      this.loadServiceLocation(serviceLocationToPreselect);
    },
  },
});
</script>

<style lang="scss" scoped>
.page-overlay {
  @include bp(m, max) {
    &:not(.modal-service-locations--no-results) {
      background: $color-action-background-neutral-default-base;
    }
  }
}

.modal-service-locations {
  :deep(.modal) {
    max-width: 542px;

    .m-header {
      padding: $pad $pad 0;

      .heading {
        font-size: map-get($font-sizes-headings, s);
        font-weight: map-get($font-weights, bold);
      }

      .headline-wrapper {
        position: unset;
        font-size: map-get($font-sizes-headings, s);
      }

      .m-actions {
        border: none;
      }
    }

    .m-content {
      padding: 0;
      background: $color-action-background-neutral-default-base;
      display: flex;
      flex-direction: column;
      border-radius: $pad;
    }

    @include bp(m, max) {
      border-radius: 0;
      max-width: unset;
      display: flex;
      flex-direction: column;

      .m-content {
        flex: 1;
      }

      .m-header {
        position: sticky;
        top: 0;
        background: $color-action-background-neutral-default-base;
      }
    }

    @include bp(m) {
      overflow: hidden;
    }
  }

  .msa-wrapper {
    padding: 0 $pad;
  }

  .msa-scrollable {
    height: 63vh;
    overflow-y: auto;
    overflow-x: hidden;

    @include bp(m, max) {
      height: unset;
    }
  }

  .msa-intro {
    display: flex;
    flex-direction: column;
    gap: $pad;
    margin-bottom: $pad;

    :deep(.checkbox-toggle) {
      padding: 0.75 * $pad 0;

      .ct-label {
        font-size: map-get($font-sizes, l);
      }
    }

    .msa-no-service-locations-notification {
      display: flex;
      flex-direction: column;
      gap: 21px;

      .msa-no-service-locations-image {
        margin: 0 auto;
        width: 160px;
        height: 160px;
      }
    }

    .i-initial-search-text {
      height: 300px;

      @include bp(m, max) {
        height: unset;
        margin-top: $pad;
      }
    }
  }

  .msa-service-locations-wrapper {
    display: flex;
    flex-direction: column;
    gap: $pad;
    margin-bottom: 1.25 * $pad;
  }

  .msa-button-wrapper {
    padding: 1.25 * $pad 0 1.25 * $pad;
    margin: auto auto 0;
    display: flex;
    justify-content: center;
    width: 100%;

    .msa-button {
      width: 307px;
    }

    @include bp(m, max) {
      position: sticky;
      bottom: 0;
      background: $color-action-background-neutral-default-base;
    }
  }

  .msa-headline {
    line-height: 22px;
    font-size: map-get($font-sizes, lxl);
    font-weight: map-get($font-weights, bold);
    margin-bottom: math.div($pad, 2);
  }

  &.modal-service-locations--map {
    .msa-map {
      margin: 0 0 $pad;

      &,
      :deep(.mapdiv) {
        min-height: 300px;
      }

      @include bp(m) {
        margin: 0;
        height: unset;
      }
    }

    :deep(.modal) {
      display: flex;
      flex-direction: column;

      .m-header {
        padding-top: 0;
        order: 1;
      }

      .msa-map {
        order: 2;
      }

      .m-content {
        order: 3;
      }
    }

    @include bp(m) {
      :deep(.modal) {
        max-width: 1100px;
        display: grid;
        grid-template-columns: 1.8fr 1fr;
        grid-template-rows: auto;
        grid-template-areas:
          'map header'
          'map content';

        .form-store-search {
          position: absolute;
          width: 55%;
          left: $pad;
          top: $pad;
        }

        .m-header {
          grid-area: header;
          padding: 0 $pad;
          display: flex;
          align-items: baseline;
          justify-content: space-between;
        }

        .m-content {
          grid-area: content;
          padding: 0;
        }
      }

      .msa-wrapper {
        display: flex;
        flex-direction: column;
        flex: 1;
        padding: 0;
      }

      .msa-map {
        grid-area: map;

        :deep(.vue-map) {
          border-bottom-left-radius: $pad;
          border-top-left-radius: $pad;
        }
      }

      .msa-scrollable {
        padding: 0 $pad;
      }

      .msa-service-locations-wrapper {
        margin-bottom: 0;
      }
    }
  }

  .fss-map-toggle {
    display: flex;
    align-self: flex-end;
    gap: 10px;

    :deep(.link) {
      color: $color-general-text-brand-default;
      font-size: map-get($font-sizes, l);
    }
  }

  .msa-show-more {
    display: flex;
    margin: $pad auto;
    font-size: map-get($font-sizes, l);
    cursor: pointer;
    color: $color-action-text-primary-default-base;

    span {
      margin-left: $pad * 0.5;
    }
  }

  &.modal-service-locations--no-results {
    @include bp(m) {
      :deep(.m-header),
      :deep(.modal),
      .msa-button-wrapper {
        border-radius: $pad;
      }
    }

    .msa-button-wrapper {
      border-top-left-radius: 0;
      border-top-right-radius: 0;
    }

    .msa-scrollable {
      height: unset;
    }
  }
}
</style>
